import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CreateDays.css';
import { helpAssistance } from '@trip-planner-monorepo/app-features';

function CreateDays({ onCreateDays }) {
  const [setupMethod, setSetupMethod] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [numberOfDays, setNumberOfDays] = useState('');
  const [error, setError] = useState('');

  // Timezone-safe helpers
  const toIsoUtc = (dateObj) => {
    if (!dateObj) return null;
    const y = dateObj.getFullYear();
    const m = dateObj.getMonth();
    const d = dateObj.getDate();
    const utc = new Date(Date.UTC(y, m, d));
    return utc.toISOString().split('T')[0];
  };

  const addDaysIsoUtc = (isoDate, offset) => {
    const [y, m, d] = isoDate.split('-').map(Number);
    const utc = new Date(Date.UTC(y, m - 1, d));
    utc.setUTCDate(utc.getUTCDate() + offset);
    return utc.toISOString().split('T')[0];
  };

  const diffDaysUtcInclusive = (start, end) => {
    const s = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
    const e = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.floor((e - s) / msPerDay) + 1;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    try {
      let days = [];

      switch (setupMethod) {
        case 'dateRange':
          if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return;
          }

          const daysDiff = diffDaysUtcInclusive(startDate, endDate);
          if (daysDiff > 60) {
            setError('Maximum trip duration is 60 days');
            return;
          }

          for (let i = 0; i < daysDiff; i++) {
            const startIso = toIsoUtc(startDate);
            days.push({
              date: addDaysIsoUtc(startIso, i),
              order: i
            });
          }
          break;

        case 'numberOfDays':
          const numDays = parseInt(numberOfDays);
          if (isNaN(numDays) || numDays < 1 || numDays > 60) {
            setError('Please enter a valid number of days (1-60)');
            return;
          }

          for (let i = 0; i < numDays; i++) {
            days.push({
              date: null,
              order: i
            });
          }
          break;

        case 'singleDay':
          days.push({
            date: startDate ? toIsoUtc(startDate) : null,
            order: 0
          });
          break;

        default:
          setError('Please select a setup method');
          return;
      }

      onCreateDays(days);
    } catch (error) {
      setError('An error occurred while creating days');
      console.error('Error creating days:', error);
    }
  };

  return (
    <div className="setup-days">
      <div className="setup-header">
        <h2>How would you like to set up your days?</h2>
        <button
          className="help-link-button"
          onClick={() => helpAssistance.showHelp('CAP-000-Getting-Started')}
          title="Learn about trip setup methods"
        >
          Need help choosing?
        </button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="setup-options">
        <div className={`setup-option ${setupMethod === 'dateRange' ? 'selected' : ''}`}>
          <h3>Set up using a date range</h3>
          <p>Choose start and end dates to automatically create days</p>
          <button onClick={() => setSetupMethod('dateRange')}>
            Use Date Range
          </button>
        </div>

        <div className={`setup-option ${setupMethod === 'numberOfDays' ? 'selected' : ''}`}>
          <h3>Set up by number of days</h3>
          <p>Specify how many days you want without setting dates</p>
          <button onClick={() => setSetupMethod('numberOfDays')}>
            Use Number of Days
          </button>
        </div>

        <div className={`setup-option ${setupMethod === 'singleDay' ? 'selected' : ''}`}>
          <h3>Start with one day</h3>
          <p>Begin with a single day and add more as needed</p>
          <button onClick={() => setSetupMethod('singleDay')}>
            Start With One Day
          </button>
        </div>
      </div>

      {setupMethod && (
        <form onSubmit={handleSubmit} className="setup-form">
          {setupMethod === 'dateRange' && (
            <div className="date-inputs">
              <div className="form-group">
                <label>Start Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="EEE dd MMM yyyy"
                  placeholderText="Select start date"
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={date => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  maxDate={startDate ? new Date(startDate.getTime() + (60 * 24 * 60 * 60 * 1000)) : undefined}
                  dateFormat="EEE dd MMM yyyy"
                  placeholderText="Select end date"
                  required
                />
              </div>
            </div>
          )}

          {setupMethod === 'numberOfDays' && (
            <div className="form-group">
              <label>Number of Days (1-60)</label>
              <input
                type="number"
                min="1"
                max="60"
                value={numberOfDays}
                onChange={(e) => setNumberOfDays(e.target.value)}
                required
              />
            </div>
          )}

          {setupMethod === 'singleDay' && (
            <div className="form-group">
              <label>Start Date (Optional)</label>
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                dateFormat="EEE dd MMM yyyy"
                placeholderText="Select a date (optional)"
                isClearable
              />
            </div>
          )}

          <div className="form-buttons">
            <button type="submit" className="primary">Create Days</button>
            <button type="button" onClick={() => setSetupMethod('')}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CreateDays;